/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl, defineMessage} from 'react-intl'
import {
    Box,
    Stack,
    Text,
    useMultiStyleConfig,
    Button,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link, useLocation} from 'react-router-dom'
//Imports
import {CartIcon} from '../../../components/icons'

//PARTIALS
import CartCta from './cart-cta'
import CartPolicies from './cart-policies'
import CartSupport from './cart-support'
import CartTitle from './cart-title'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import CartRemoveWarning from './cart-remove-warning'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'

const EmptyCart = ({removeMessages = []}) => {
    const style = useMultiStyleConfig('Cart', {removeMessagesLength: removeMessages.length})
    const {locale} = useMultiSite()
    const {formatMessage} = useIntl()
    const {customParams} = getConfig()
    const location = useLocation()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const cartTitleMessage = defineMessage({
        defaultMessage: 'Basket',
        id: 'cart_title.title.cart_title',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: formatMessage(cartTitleMessage),
        description: formatMessage(cartTitleMessage),
        url: currentPath,
    }

    return (
        <Box data-testid="sf-cart-empty" {...style?.emptyCart?.pageContainer} data-gtm="cart-page">
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <CartTitle />
            <Box {...style?.emptyCart?.cartContainer}>
                <Stack {...style?.emptyCart?.leftStack}>
                    {removeMessages.length > 0 && (
                        <Box {...style?.emptyCart?.removeMessagesBox}>
                            {removeMessages.map((msg) => (
                                <CartRemoveWarning key={msg.id} message={msg.text} />
                            ))}
                        </Box>
                    )}
                    <Box {...style?.emptyCart?.cartContent}>
                        <CartIcon {...style?.emptyCart?.emptyIcon} aria-hidden="true" />
                        <Stack {...style?.emptyCart?.emptyStack}>
                            <Text {...style?.emptyCart?.emptyText}>
                                <FormattedMessage
                                    defaultMessage="Your basket is empty."
                                    id="empty_cart.description.empty_cart"
                                />
                            </Text>
                            <Button
                                variant="primary"
                                as={Link}
                                to={`/${locale?.alias}`}
                                {...style?.emptyCart?.discoverMoreButton}
                                aria-label={formatMessage({
                                    id: 'empty_cart.button.discover.label',
                                    defaultMessage: 'Continue shopping and discover more products',
                                })}
                            >
                                <FormattedMessage
                                    defaultMessage="Discover More"
                                    id="cart_default.cart_support.discover_more_button"
                                />
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
                <Stack {...style?.emptyCart?.checkoutContainer}>
                    {/** gifts and returns section */}
                    <Box {...style?.cart?.checkoutItem}>
                        <CartPolicies />
                    </Box>
                    {/**Support Section */}
                    <Box {...style?.cart?.checkoutItemWhite}>
                        <CartSupport />
                    </Box>
                    {/**Payments Section */}
                    <Box {...style?.cart?.checkoutItemWhite}>
                        <CartCta />
                    </Box>
                </Stack>
            </Box>
            <Box id="cart_product_recommendations" overflowX="hidden"></Box>
        </Box>
    )
}
EmptyCart.propTypes = {
    isRegistered: PropTypes.bool,
    removeMessages: PropTypes.array,
}

export default EmptyCart
